/**
 * useDashboard — fetches `/api/dashboard`. The org-wide master Kanban:
 * every card from every project the caller is a member of, bucketed
 * into the canonical 5-column shape (Inbox / Up Next / Today / Review /
 * Done).
 *
 * Pairs with `3mote-be-new/src/dashboard/dashboard-route.ts`. The
 * response carries:
 *
 *   - `cards[]` — every visible card, each tagged with `projectId`
 *     (which project it belongs to) and `columnName` (server-derived
 *     bucket — for events/announcements via on-read coercion, for
 *     tasks/tickets via `project_status.name` lookup, fallback Inbox).
 *   - `projects[]` — `{ id, name, code, statuses: [{ id, name }] }`
 *     for every project that contributes to `cards[]`. The FE uses
 *     this to (a) render the project chip on each card and (b)
 *     resolve column-name → status-id when dragging a card to a new
 *     column on the dashboard.
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { components } from '@/api/generated/api'
import type { BoardCard } from '@/api/types-bridge'
import { seedWorkingAgents } from '@/features/agents/use-agent-working'

/**
 * Wave 4 Batch K — `DashboardCard.projectId` is now `string | null`.
 * Tenant-wide announcements have no project (`projectId === null`) and
 * appear in EVERY project's board AND on the master dashboard. Until
 * the next OpenAPI codegen lands, this override widens the field. Note
 * that `BoardCard` itself was widened by `types-bridge.ts` (assignee
 * fields optional + `attendees` array), and we layer that here so a
 * `DashboardCard` is a `BoardCard` with the dashboard's extra metadata.
 */
export type DashboardCard = BoardCard & {
  /** Wave 4 Batch K — `null` for tenant-wide announcements. */
  readonly projectId: string | null
  /** Server-derived column bucket — one of the canonical 5 names. */
  readonly columnName: 'Inbox' | 'Up Next' | 'Today' | 'Review' | 'Done'
  /**
   * When a task/ticket reached `done_at` (the Done-column visibility
   * stamp), serialized on the dashboard payload for the Pulse layout's
   * "Just shipped" column. Type derived from the generated
   * `DashboardCard` schema rather than hand-typed, so a future wire-shape
   * change here is a type error instead of a silent drift. `null` on a
   * task/ticket that hasn't reached Done; absent on events and
   * announcements, which have no `done_at` column.
   */
  readonly doneAt?: components['schemas']['DashboardCard']['doneAt']
}

/**
 * Wave 4 Batch K — `DashboardProject.statuses[]` may carry `allowsTimer`
 * once the BE codegen catches up. Optional today; the FE falls open
 * (timer button enabled) when absent.
 */
export type DashboardProject = Omit<components['schemas']['DashboardProject'], 'statuses'> & {
  readonly statuses: ReadonlyArray<
    components['schemas']['DashboardProjectStatusRef'] & {
      readonly allowsTimer?: boolean
    }
  >
}

export type DashboardData = Omit<components['schemas']['DashboardData'], 'cards' | 'projects'> & {
  readonly cards: readonly DashboardCard[]
  readonly projects: readonly DashboardProject[]
}

export const DASHBOARD_QUERY_KEY = ['dashboard'] as const

export function useDashboard() {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEY,
    queryFn: async () => {
      const requestedAt = Date.now()
      const res = await apiClient<{ data: DashboardData }>('/api/dashboard')
      seedWorkingAgents(res.data.cards, requestedAt)
      return res.data
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
